import { DecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import type { DOMExportOutput, ElementFormatType } from "lexical";
import type { SerializedSurveyAnswersNode } from "./types";

export class SurveyAnswersNode extends DecoratorBlockNode {
  __surveyId: string;
  __questionBold: boolean;
  __questionItalic: boolean;
  __questionColor: string;
  __answerBold: boolean;
  __answerItalic: boolean;
  __answerColor: string;
  __divider: boolean;

  constructor(
    surveyId = "",
    questionBold = false,
    questionItalic = false,
    questionColor = "#000000",
    answerBold = false,
    answerItalic = false,
    answerColor = "#000000",
    divider = false,
    format?: ElementFormatType,
    key?: string,
  ) {
    super(format, key);
    this.__surveyId = surveyId;
    this.__questionBold = questionBold;
    this.__questionItalic = questionItalic;
    this.__questionColor = questionColor;
    this.__answerBold = answerBold;
    this.__answerItalic = answerItalic;
    this.__answerColor = answerColor;
    this.__divider = divider;
  }

  static getType(): string {
    return "survey-answers";
  }

  static clone(node: SurveyAnswersNode): SurveyAnswersNode {
    return new this(
      node.__surveyId,
      node.__questionBold,
      node.__questionItalic,
      node.__questionColor,
      node.__answerBold,
      node.__answerItalic,
      node.__answerColor,
      node.__divider,
      node.__format,
      node.__key,
    );
  }

  static importJSON(serializedNode: SerializedSurveyAnswersNode): SurveyAnswersNode {
    return new this(
      serializedNode.surveyId,
      serializedNode.questionBold,
      serializedNode.questionItalic,
      serializedNode.questionColor,
      serializedNode.answerBold,
      serializedNode.answerItalic,
      serializedNode.answerColor,
      serializedNode.divider,
      serializedNode.format,
    );
  }

  exportJSON(): SerializedSurveyAnswersNode {
    return {
      ...super.exportJSON(),
      type: "survey-answers",
      version: 1,
      surveyId: this.__surveyId,
      questionBold: this.__questionBold,
      questionItalic: this.__questionItalic,
      questionColor: this.__questionColor,
      answerBold: this.__answerBold,
      answerItalic: this.__answerItalic,
      answerColor: this.__answerColor,
      divider: this.__divider,
    };
  }

  decorate(): React.JSX.Element {
    return <></>;
  }

  exportDOM(): DOMExportOutput {
    const dom = document.createElement("div");
    dom.setAttribute("data-node-type", "survey-answers");
    return { element: dom };
  }

  // Getters
  getSurveyId(): string {
    return this.getLatest().__surveyId;
  }

  getQuestionBold(): boolean {
    return this.getLatest().__questionBold;
  }

  getQuestionItalic(): boolean {
    return this.getLatest().__questionItalic;
  }

  getQuestionColor(): string {
    return this.getLatest().__questionColor;
  }

  getAnswerBold(): boolean {
    return this.getLatest().__answerBold;
  }

  getAnswerItalic(): boolean {
    return this.getLatest().__answerItalic;
  }

  getAnswerColor(): string {
    return this.getLatest().__answerColor;
  }

  getDivider(): boolean {
    return this.getLatest().__divider;
  }

  // Setters
  setQuestionBold(v: boolean): void {
    this.getWritable().__questionBold = v;
  }

  setQuestionItalic(v: boolean): void {
    this.getWritable().__questionItalic = v;
  }

  setQuestionColor(v: string): void {
    this.getWritable().__questionColor = v;
  }

  setAnswerBold(v: boolean): void {
    this.getWritable().__answerBold = v;
  }

  setAnswerItalic(v: boolean): void {
    this.getWritable().__answerItalic = v;
  }

  setAnswerColor(v: string): void {
    this.getWritable().__answerColor = v;
  }

  setSurveyId(v: string): void {
    this.getWritable().__surveyId = v;
  }

  setDivider(v: boolean): void {
    this.getWritable().__divider = v;
  }
}
